import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";

const COLORS = ["#FFD700", "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD", "#98D8C8"];
const COIN_COUNT = 10;
const CONFETTI_COUNT = 28;

function randomBetween(min, max) {
  return min + Math.random() * (max - min);
}

function Particle({ x, color, delay, type }) {
  if (type === "coin") {
    return (
      <motion.div
        className="fixed pointer-events-none z-[9999] text-3xl select-none"
        style={{ left: `${x}%`, top: "50%", fontSize: randomBetween(20, 36) }}
        initial={{ y: 0, opacity: 1, scale: 0.5, rotate: 0 }}
        animate={{
          y: [0, -randomBetween(120, 250), randomBetween(100, 300)],
          opacity: [0, 1, 1, 0],
          scale: [0.5, 1.2, 1, 0.6],
          rotate: [0, randomBetween(-30, 30)],
        }}
        transition={{ duration: randomBetween(1.2, 2), delay, ease: "easeOut" }}
      >
        🪙
      </motion.div>
    );
  }

  const size = randomBetween(6, 14);
  return (
    <motion.div
      className="fixed pointer-events-none z-[9999] rounded-sm"
      style={{
        left: `${x}%`,
        top: "-2%",
        width: size,
        height: size,
        background: color,
      }}
      initial={{ y: 0, opacity: 1, rotate: 0, scaleX: 1 }}
      animate={{
        y: `${randomBetween(60, 110)}vh`,
        x: randomBetween(-80, 80),
        opacity: [1, 1, 0],
        rotate: randomBetween(180, 720),
        scaleX: [1, 0.4, 1, 0.4],
      }}
      transition={{ duration: randomBetween(1.5, 2.8), delay, ease: "easeIn" }}
    />
  );
}

export default function CoinCelebration() {
  const [celebrations, setCelebrations] = useState([]);
  const [spends, setSpends] = useState([]);

  const triggerCelebration = useCallback((e) => {
    const amount = e?.detail?.amount || 0;
    const id = Date.now() + Math.random();

    const particles = [
      ...Array.from({ length: COIN_COUNT }, (_, i) => ({
        id: `coin-${id}-${i}`,
        type: "coin",
        x: randomBetween(5, 95),
        delay: randomBetween(0, 0.6),
      })),
      ...Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
        id: `conf-${id}-${i}`,
        type: "confetti",
        x: randomBetween(0, 100),
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        delay: randomBetween(0, 0.4),
      })),
    ];

    setCelebrations(prev => [...prev, { id, particles, amount }]);
    setTimeout(() => {
      setCelebrations(prev => prev.filter(c => c.id !== id));
    }, 3200);
  }, []);

  const triggerSpend = useCallback((e) => {
    const amount = e?.detail?.amount || 0;
    const id = Date.now() + Math.random();
    setSpends(prev => [...prev, { id, amount }]);
    setTimeout(() => {
      setSpends(prev => prev.filter(s => s.id !== id));
    }, 2000);
  }, []);

  useEffect(() => {
    window.addEventListener("howecoin-earned", triggerCelebration);
    window.addEventListener("howecoin-spent", triggerSpend);
    return () => {
      window.removeEventListener("howecoin-earned", triggerCelebration);
      window.removeEventListener("howecoin-spent", triggerSpend);
    };
  }, [triggerCelebration, triggerSpend]);

  return (
    <>
      <AnimatePresence>
        {celebrations.map(({ id, particles, amount }) => (
          <div key={id}>
            {particles.map(p => (
              <Particle key={p.id} {...p} />
            ))}
            <motion.div
              className="fixed pointer-events-none z-[9999] left-1/2 top-1/3"
              style={{ transform: "translateX(-50%)" }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [0, 1.3, 1], opacity: [0, 1, 1, 0] }}
              transition={{ duration: 2.5, times: [0, 0.2, 0.5, 1] }}
            >
              <div
                className="bg-yellow-400 text-white font-black rounded-full px-5 py-3 shadow-2xl flex items-center gap-2"
                style={{ fontSize: 28, fontFamily: "'Fredoka One', cursive", whiteSpace: "nowrap" }}
              >
                +{amount} 🪙
              </div>
            </motion.div>
          </div>
        ))}
      </AnimatePresence>

      <AnimatePresence>
        {spends.map(({ id, amount }) => (
          <motion.div
            key={id}
            className="fixed pointer-events-none z-[9999] left-1/2 top-1/3"
            style={{ transform: "translateX(-50%)" }}
            initial={{ scale: 0, opacity: 0, y: 0 }}
            animate={{ scale: [0, 1.2, 1], opacity: [0, 1, 1, 0], y: [0, -20, -40] }}
            transition={{ duration: 1.8, times: [0, 0.2, 0.6, 1] }}
          >
            <div
              className="font-black rounded-full px-5 py-3 shadow-2xl flex items-center gap-2"
              style={{
                fontSize: 26, fontFamily: "'Fredoka One', cursive", whiteSpace: "nowrap",
                background: "linear-gradient(135deg,#FF6B6B,#E84040)",
                color: "white",
              }}
            >
              -{amount} 🪙
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </>
  );
}
